import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Menu } from "primereact/menu";
import authApi from "../../../../service/IdApi";
import PageLoading from "../../../../components/PageLoading";
import PageLoadError from "../../../../components/PageLoadError";
import PageHeading from "../../../../components/PageHeading";
import RecommendationsSubNav from "./RecommendationsSubNav";
import RecommendationCatalogDialog from "./RecommendationCatalogDialog";
import { useAppContext } from "../../../../context/appContext";

const RecommendationSummary = () => {
    const { displayAlert } = useAppContext();
    const queryClient = useQueryClient();
    const menu = useRef(null);
    const [formVisible, setFormVisible] = useState(false);
    const [formMode, setFormMode] = useState("add");
    const [selected, setSelected] = useState(null);
    const [actionRow, setActionRow] = useState(null);
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const { isLoading, isError, data } = useQuery(["RecommendationSummary"], async () => {
        const result = await authApi.get("admin/recommendations/summary");
        return result.data.recommendations || [];
    });

    const { data: options } = useQuery(["RecommendationCatalogOptions"], async () => {
        const result = await authApi.get("admin/recommendations/catalog/options");
        return result.data;
    });

    const pageHeader = (
        <PageHeading pageTitle="Admin - Recommendation Counts">
            <RecommendationsSubNav active="summary" />
        </PageHeading>
    );

    if (isLoading) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const openAdd = () => {
        setSelected(null);
        setFormMode("add");
        setFormVisible(true);
    };

    const openEdit = (rowData) => {
        setSelected(rowData);
        setFormMode("edit");
        setFormVisible(true);
    };

    const handleRowClick = (event) => {
        const target = event.originalEvent.target;
        if (target.closest("button")) {
            return;
        }
        openEdit(event.data);
    };

    const openDelete = (rowData) => {
        setSelected(rowData);
        setDeleteVisible(true);
    };

    const refreshCatalog = async () => {
        await queryClient.invalidateQueries(["RecommendationSummary"]);
        await queryClient.invalidateQueries(["RecommendationCatalogOptions"]);
    };

    const confirmDelete = async () => {
        if (!selected) return;
        setDeleting(true);
        try {
            await authApi.delete("admin/recommendations/catalog/" + encodeURIComponent(selected.name));
            displayAlert("success", "Recommendation Deleted", `${selected.name} was deleted.`);
            await refreshCatalog();
        } catch (err) {
            console.error(err);
            displayAlert("error", "Unable to Delete", "Please try again later.");
        } finally {
            setDeleting(false);
            setDeleteVisible(false);
        }
    };

    const effortBodyTemplate = (rowData) => `${rowData.value} / ${rowData.priority} / ${rowData.effort}`;

    const emailableBodyTemplate = (rowData) => (rowData.emailable ? "Yes" : "No");

    const enabledBodyTemplate = (rowData) => (rowData.enabled !== false ? "Yes" : "No");

    const rowClassName = (rowData) => (rowData.enabled === false ? "text-500" : "");

    const rowActions = [
        {
            label: "Edit",
            icon: "pi pi-pencil",
            command: () => {
                openEdit(actionRow);
            },
        },
        {
            label: "Delete",
            icon: "pi pi-trash",
            command: () => {
                openDelete(actionRow);
            },
        },
    ];

    const showActionMenu = (e, rowData) => {
        setActionRow(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    return (
        <div>
            {pageHeader}
            <RecommendationCatalogDialog
                visible={formVisible}
                onHide={() => setFormVisible(false)}
                mode={formMode}
                recommendation={selected}
                options={options}
                onSaved={refreshCatalog}
            />
            <Dialog
                visible={deleteVisible}
                style={{ width: "450px" }}
                header="Delete Recommendation"
                modal
                footer={
                    <div>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteVisible(false)} disabled={deleting} />
                        <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={confirmDelete} loading={deleting} autoFocus />
                    </div>
                }
                onHide={() => setDeleteVisible(false)}
            >
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                    <span>
                        Delete <strong>{selected?.name}</strong> from the catalog? Existing provider statuses for it will be pruned the next time their recommendations are rebuilt.
                    </span>
                </div>
            </Dialog>
            <Menu model={rowActions} popup ref={menu} />
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex justify-content-end py-3">
                        <Button label="Add Recommendation" icon="pi pi-plus" onClick={openAdd} />
                    </div>
                    <DataTable
                        value={data}
                        dataKey="name"
                        selectionMode="single"
                        onRowClick={handleRowClick}
                        paginator
                        rows={50}
                        rowsPerPageOptions={[25, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} recommendations"
                        emptyMessage="No recommendations found."
                        sortField="score"
                        sortOrder={-1}
                        size="small"
                        rowClassName={rowClassName}
                    >
                        <Column body={actionBodyTemplate} header="Actions" style={{ width: "5rem" }} />
                        <Column field="name" header="Name" sortable />
                        <Column field="enabled" header="Enabled" body={enabledBodyTemplate} sortable />
                        <Column field="emailable" header="Emailable" body={emailableBodyTemplate} sortable />
                        <Column field="score" header="Score" sortable />
                        <Column header="Value / Priority / Effort" body={effortBodyTemplate} />
                        <Column field="activeCount" header="Active" sortable />
                        <Column field="completedCount" header="Completed" sortable />
                        <Column field="snoozedCount" header="Snoozed" sortable />
                        <Column field="dismissedCount" header="Dismissed" sortable />
                        <Column field="totalCount" header="Total" sortable />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default RecommendationSummary;
